import type { Quality, DownloadProgress, DownloadResult, BulkDownloadStatus, SSEProgress } from '../types'
import { getAuthHeader } from './firebaseAuth'

export async function downloadVideo(
  url: string,
  quality: Quality,
  onProgress?: (progress: DownloadProgress) => void,
  signal?: AbortSignal
): Promise<DownloadResult> {
  const apiUrl = `/api/download?url=${encodeURIComponent(url)}&quality=${quality}`

  // Required as of SHUT-23: this route performs a server-side fetch of a caller-supplied URL, so
  // it is no longer open to anonymous callers.
  const response = await fetch(apiUrl, { headers: await getAuthHeader(), signal })

  if (response.status === 401) {
    throw new Error('Sign in to download videos')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Download failed (${response.status})`)
  }

  const contentDisposition = response.headers.get('Content-Disposition') || ''
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
  const filename = filenameMatch ? filenameMatch[1] : 'video.mp4'

  const contentLength = response.headers.get('Content-Length')
  const total = contentLength ? parseInt(contentLength, 10) : 0

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Failed to read response stream')
  }

  const chunks: ArrayBuffer[] = []
  let received = 0

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      chunks.push(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength))
      received += value.length

      if (onProgress && total > 0) {
        onProgress({
          received,
          total,
          percent: Math.round((received / total) * 100),
        })
      }
    }
  } finally {
    reader.releaseLock()
  }

  if (onProgress) {
    onProgress({ received, total: received, percent: 100 })
  }

  const contentType = response.headers.get('Content-Type') || 'video/mp4'
  const blob = new Blob(chunks, { type: contentType })
  const file = new File([blob], filename, { type: contentType })
  const objectUrl = URL.createObjectURL(blob)

  return {
    file,
    objectUrl,
    filename,
    contentType,
  }
}

export interface BulkDownloadResult {
  blob: Blob
  filename: string
}

export async function bulkDownloadVideos(
  urls: string[],
  quality: Quality,
  onProgress?: (progress: DownloadProgress) => void,
  signal?: AbortSignal
): Promise<BulkDownloadResult> {
  const response = await fetch('/api/bulk-download', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getAuthHeader()),
    },
    body: JSON.stringify({ urls, quality }),
    signal,
  })

  if (response.status === 401) {
    throw new Error('Sign in to download videos')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as BulkDownloadStatus | { error?: string }

    if ('results' in errorData && errorData.results) {
      const failedUrls = errorData.results
        .filter(r => !r.success)
        .map(r => r.error || 'Unknown error')
        .join('; ')
      throw new Error(`All downloads failed: ${failedUrls}`)
    }

    throw new Error((errorData as { error?: string }).error || `Bulk download failed (${response.status})`)
  }

  const contentLength = response.headers.get('Content-Length')
  const total = contentLength ? parseInt(contentLength, 10) : 0

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Failed to read response stream')
  }

  const chunks: ArrayBuffer[] = []
  let received = 0

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      chunks.push(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength))
      received += value.length

      if (onProgress && total > 0) {
        onProgress({
          received,
          total,
          percent: Math.round((received / total) * 100),
        })
      }
    }
  } finally {
    reader.releaseLock()
  }

  if (onProgress) {
    onProgress({ received, total: received, percent: 100 })
  }

  const blob = new Blob(chunks, { type: 'application/zip' })

  return {
    blob,
    filename: 'videos.zip',
  }
}

/**
 * Streams download progress.
 *
 * Uses `fetch` + `ReadableStream` rather than `EventSource`. `EventSource` cannot send an
 * `Authorization` header, and `/api/download-progress` is not merely a progress channel — it is the
 * route that performs the server-side fetch, so it requires a session (SHUT-23). The alternative,
 * a token in the query string, would put a credential in access logs and browser history.
 *
 * Returns a cancel function, preserving the contract `useVideoDownload` already relies on.
 */
export function downloadVideoSSE(
  url: string,
  quality: Quality,
  onProgress: (progress: SSEProgress) => void,
  onComplete: (downloadId: string, filename: string, size: number) => void,
  onError: (error: string) => void
): () => void {
  const params = new URLSearchParams({ url, quality })
  const controller = new AbortController()

  const dispatch = (raw: string): boolean => {
    let data: SSEProgress
    try {
      data = JSON.parse(raw)
    } catch {
      onError('Failed to parse server response')
      return true
    }

    onProgress(data)

    if (data.phase === 'complete' && data.downloadId) {
      onComplete(data.downloadId, data.filename || 'video.mp4', data.size || 0)
      return true
    }
    if (data.phase === 'error') {
      onError(data.message || 'Download failed')
      return true
    }
    return false
  }

  const run = async () => {
    let response: Response
    try {
      response = await fetch(`/api/download-progress?${params}`, {
        headers: await getAuthHeader(),
        signal: controller.signal,
      })
    } catch {
      if (!controller.signal.aborted) onError('Connection lost')
      return
    }

    if (response.status === 401) {
      onError('Sign in to download videos')
      return
    }
    if (!response.ok || !response.body) {
      onError(`Download failed (${response.status})`)
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // SSE frames are separated by a blank line. Anything after the last separator is a
        // partial frame and stays buffered until the rest of it arrives.
        let separator = buffer.indexOf('\n\n')
        while (separator !== -1) {
          const frame = buffer.slice(0, separator)
          buffer = buffer.slice(separator + 2)

          const payload = frame
            .split('\n')
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).trim())
            .join('\n')

          if (payload && dispatch(payload)) {
            controller.abort()
            return
          }
          separator = buffer.indexOf('\n\n')
        }
      }
      // The server closed without ever sending a terminal frame.
      onError('Connection lost')
    } catch {
      if (!controller.signal.aborted) onError('Connection lost')
    } finally {
      reader.releaseLock()
    }
  }

  void run()

  return () => controller.abort()
}

/**
 * Publishes a shareable (Discord-embeddable) link for a source URL and returns it. The server
 * re-fetches the video and stores it permanently, so this is deliberately on-demand rather than
 * something every download triggers.
 *
 * Requires a signed-in user — what it creates is permanent and publicly linkable, so the server
 * will not publish for an anonymous caller.
 */
export async function createShareLink(sourceUrl: string): Promise<string> {
  const response = await fetch('/api/share-link', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getAuthHeader()),
    },
    body: JSON.stringify({ source_url: sourceUrl }),
  })

  if (response.status === 401) {
    throw new Error('Your session expired — sign in again to create a share link')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Could not create share link (${response.status})`)
  }

  const data = await response.json().catch(() => ({}))
  if (!data.embed_url) {
    throw new Error('Server did not return a share link')
  }

  return data.embed_url as string
}

/**
 * Content type for a download the server labelled `application/octet-stream`.
 *
 * SHUT-175: this used to be `filename.endsWith('.gif') ? 'image/gif' : 'video/mp4'`, which was
 * complete only while every download was a video or a GIF. Photos download as `.png`/`.jpg`, and
 * calling one `video/mp4` makes the saved file unopenable. The normal paths send a real type, so
 * this is the fallback for the octet-stream case only.
 */
function contentTypeForFilename(filename: string): string {
  const extension = filename.split('.').pop()?.toLowerCase() ?? ''
  const byExtension: Record<string, string> = {
    gif: 'image/gif',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    mp4: 'video/mp4',
  }
  return byExtension[extension] ?? 'video/mp4'
}

export async function fetchCachedVideo(
  downloadId: string,
  expectedFilename?: string
): Promise<DownloadResult> {
  // `format=json` rather than following the 302. This request is authenticated (SHUT-23), and a
  // request carrying an Authorization header cannot be redirected to storage.googleapis.com: the
  // header is not CORS-safelisted, so the cross-origin hop needs a preflight, and GCS answers that
  // preflight with no CORS headers. Safari surfaced it as `TypeError: Load failed` with nothing in
  // the server logs past the 302. Asking for the URL keeps the credential on this hop and lets the
  // byte fetch below run clean.
  const response = await fetch(`/api/download/${downloadId}?format=json`, {
    headers: await getAuthHeader(),
  })

  if (response.status === 401) {
    throw new Error('Sign in to download videos')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Download failed (${response.status})`)
  }

  // Prefer the name the SSE 'complete' event already gave us. When GCS storage is enabled the
  // route redirects to a signed storage.googleapis.com URL, and that object carries no
  // Content-Disposition header — so the header below is only a fallback for the local-file path.
  const contentDisposition = response.headers.get('Content-Disposition') || ''
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)

  // The server answers with JSON only when GCS storage is on. Without it there is no redirect to
  // avoid and the body IS the file, so that path stays exactly as it was.
  const isSignedUrlResponse = (response.headers.get('Content-Type') || '')
    .includes('application/json')

  if (isSignedUrlResponse) {
    const signed = await response.json()
    const filename = expectedFilename || signed.filename || 'video.mp4'
    const contentType = signed.contentType || 'video/mp4'

    // Deliberately no headers: an Authorization header here is what broke the redirect, and the
    // signature in the URL is this hop's only credential.
    const stored = await fetch(signed.url)
    if (!stored.ok) {
      throw new Error(`Could not retrieve the video from storage (${stored.status})`)
    }

    const blob = new Blob([await stored.blob()], { type: contentType })
    return {
      file: new File([blob], filename, { type: contentType }),
      objectUrl: URL.createObjectURL(blob),
      filename,
      contentType,
    }
  }

  const filename = expectedFilename || (filenameMatch ? filenameMatch[1] : 'video.mp4')

  const rawContentType = response.headers.get('Content-Type') || 'video/mp4'
  const contentType = rawContentType === 'application/octet-stream'
    ? contentTypeForFilename(filename)
    : rawContentType
  const rawBlob = await response.blob()
  const blob = new Blob([rawBlob], { type: contentType })
  const file = new File([blob], filename, { type: contentType })
  const objectUrl = URL.createObjectURL(blob)

  return {
    file,
    objectUrl,
    filename,
    contentType,
  }
}
